import React from "react";

import { getUserFromToken } from "../../../Context/functions";
import { useGlobalContext } from "../../../Context/global.context";

const OPENAI_CHAT_URL =
  process.env.REACT_APP_OPENAI_API_URL || "/api/chat";

const LAB_FIELDS = [
  ["cholesterol", "Cholesterol"],
  ["triglyceride", "Triglyceride"],
  ["hdl", "HDL"],
  ["ldl", "LDL"],
  ["nonHdl", "Non-HDL"],
  ["cholesterolHdlRatio", "Cholesterol/HDL Ratio"],
  ["creatineKinase", "Creatine Kinase"],
  ["alanineAminotransferase", "ALT (Alanine Aminotransferase)"],
  ["lipoproteinA", "Lipoprotein A"],
  ["apolipoproteinB", "Apolipoprotein B"],
  ["natriureticPeptideB", "BNP"],
  ["urea", "Urea"],
  ["creatinine", "Creatinine"],
  ["gfr", "GFR"],
  ["albumin", "Albumin"],
  ["sodium", "Sodium"],
  ["potassium", "Potassium"],
  ["vitaminB12", "Vitamin B12"],
  ["ferritin", "Ferritin"],
  ["hemoglobinA1C", "Hemoglobin A1C"],
  ["urineAlbumin", "Urine Albumin"],
  ["albuminCreatinineRatio", "Albumin/Creatinine Ratio"],
];

const readResponseBody = async (response) => {
  const text = await response.text();

  try {
    return text ? JSON.parse(text) : {};
  } catch (error) {
    throw new Error(`Unexpected response (${response.status}): ${text.slice(0, 120)}`);
  }
};

const isAboveZero = (value) => {
  if (value === null || value === undefined) return false;
  const normalized = String(value).replace(/,/g, "").trim();
  if (!normalized) return false;

  const numericValue = Number(normalized);
  return Number.isFinite(numericValue) && numericValue > 0;
};

const parseConditionCodes = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }

  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const buildConditionMap = (conditionData) => {
  const list = Array.isArray(conditionData)
    ? conditionData
    : conditionData && typeof conditionData === "object"
      ? Object.values(conditionData)
      : [];

  const map = new Map();

  for (const condition of list) {
    const code = String(condition?.conditionCode ?? condition?.code ?? "").trim().toUpperCase();
    const label = String(condition?.conditionName ?? condition?.name ?? code).trim();
    if (code) map.set(code, label || code);
  }

  return map;
};

const readMedicationId = (medication) => String(medication?.ID ?? medication?.id ?? "").trim();
const readMedicationDin = (medication) => String(medication?.DIN ?? medication?.din ?? "").replace(/\D+/g, "").slice(0, 8);
const readMedicationName = (medication) =>
  String(medication?.medication_name ?? medication?.medication ?? medication?.name ?? "").trim();
const readMedicationDose = (medication) =>
  String(medication?.medication_dose ?? medication?.defaultDose ?? medication?.dose ?? "").trim();
const readMedicationCategory = (medication) =>
  String(medication?.medication_cat ?? medication?.category ?? "").trim();

const parseMedicationTokens = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && typeof item === "object") {
          return String(item.ID ?? item.id ?? item.DIN ?? item.din ?? "").trim();
        }
        return String(item || "").trim();
      })
      .filter(Boolean);
  }

  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const buildMedicationIndexes = (medsArray) => {
  const list = Array.isArray(medsArray) ? medsArray : [];
  const byId = new Map();
  const byDin = new Map();

  for (const medication of list) {
    const id = readMedicationId(medication);
    const din = readMedicationDin(medication);
    if (id) byId.set(id, medication);
    if (din) byDin.set(din, medication);
  }

  return { byId, byDin };
};

const calculateAge = (dob) => {
  if (!dob) return "-";
  const birthDate = new Date(dob);
  if (isNaN(birthDate.getTime())) return "-";
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return age >= 0 ? age : "-";
};

const buildPatientSummary = ({ patient, conditionData, medsArray }) => {
  const sourcePatient = patient || {};
  const conditionMap = buildConditionMap(conditionData);
  const medicationIndexes = buildMedicationIndexes(medsArray);
  const conditions = parseConditionCodes(sourcePatient?.conditionData || sourcePatient?.conditions)
    .map((code) => {
      const normalizedCode = String(code || "").trim().toUpperCase();
      return {
        code: normalizedCode,
        label: conditionMap.get(normalizedCode) || String(code || "").trim(),
      };
    })
    .filter((condition) => condition.code || condition.label);
  const medicationTokens = parseMedicationTokens(sourcePatient?.medsData || sourcePatient?.medications);
  const medications = medicationTokens
    .map((token) => {
      const rawToken = String(token || "").trim();
      const dinToken = rawToken.replace(/\D+/g, "").slice(0, 8);
      const masterMedication = medicationIndexes.byId.get(rawToken) || medicationIndexes.byDin.get(dinToken);

      return {
        id: readMedicationId(masterMedication) || rawToken,
        din: readMedicationDin(masterMedication) || (dinToken.length === 8 ? dinToken : ""),
        name: readMedicationName(masterMedication) || rawToken,
        dose: readMedicationDose(masterMedication),
        category: readMedicationCategory(masterMedication),
      };
    })
    .filter((medication) => medication.name);
  const labs = LAB_FIELDS.map(([key, label]) => ({
    key,
    label,
    value: sourcePatient?.[key] ?? "",
    date: sourcePatient?.[`${key}Date`] ?? "",
  })).filter((lab) => isAboveZero(lab.value));

  return {
    patientName: sourcePatient?.clientName || sourcePatient?.name || "",
    age: calculateAge(sourcePatient?.dateOfBirth),
    sex: sourcePatient?.sex || sourcePatient?.gender || "",
    conditions,
    medications,
    labs,
  };
};

const buildSummaryPromptLines = (summary) => {
  const labLines = (summary?.labs || [])
    .map((lab) => `- ${lab.label}: ${lab.value}${lab.date ? ` (${lab.date})` : ""}`)
    .join("\n");
  const conditionLines = (summary?.conditions || [])
    .map((condition) => `- ${condition.label}${condition.code && condition.code !== condition.label ? ` (${condition.code})` : ""}`)
    .join("\n");
  const medicationLines = (summary?.medications || [])
    .map((medication) => {
      const details = [
        medication.dose,
        medication.category,
        medication.din ? `DIN ${medication.din}` : "",
      ].filter(Boolean).join("; ");

      return `- ${medication.name}${details ? ` (${details})` : ""}`;
    })
    .join("\n");

  return {
    labLines,
    conditionLines,
    medicationLines,
  };
};

export const buildMedcheckAiPrompt = (summary) => {
  const { labLines, conditionLines, medicationLines } = buildSummaryPromptLines(summary);

  return [
    "GDMT.ca MASTER PROMPT (v2.1 - OPTIMIZED HYBRID ARCHITECTURE)",
    "",
    "SYSTEM ROLE",
    "You are a deterministic clinical decision engine and documentation generator designed to identify care gaps in ASCVD, CKD, diabetes, and heart failure, apply guideline-directed medical therapy (GDMT), and generate Ontario MedsCheck documentation.",
    "You function as a deterministic, guideline-bound clinical execution system, not a general AI assistant.",
    "",
    "CORE OBJECTIVE",
    "For every patient:",
    "1. Identify undertreated disease states, missing therapies, suboptimal dosing, missing labs, target failures, and optimization opportunities.",
    "2. Apply Canadian guidelines, KDIGO guidance, GDMT.ca execution logic, and Ontario program requirements.",
    "3. Generate output that is clinically precise, audit-ready, prescriber-friendly, pharmacist-usable, patient-comprehensible, and Ontario-program compliant.",
    "",
    "SYSTEM ARCHITECTURE",
    "Operate in 5 sequential layers:",
    "Layer 1 - Structured patient extraction.",
    "Layer 2 - Deterministic clinical logic engine.",
    "Layer 3 - Finding prioritization.",
    "Layer 4 - MedsCheck report generation.",
    "Layer 5 - Output formatting/rendering.",
    "Use the same structured patient dataset for all reasoning.",
    "",
    "SOURCE-OF-TRUTH FILE REFERENCES",
    "Use the uploaded documents below as the clinical authority when relevant. These files are expected to exist in the configured vector store.",
    "[[doc: CCS Guidelines for use of SGLT2i and GLP 1 agonists 2022.pdf]]",
    "[[doc: 2022 CCS Combined Cardiovascular Guidelines.pdf]]",
    "[[doc: CCS Dyslipidemia Guidelines 2021-v20250106192216.pdf]]",
    "[[doc: KDIGO 2024 CKD Guideline.pdf]]",
    "[[doc: 2025 Chronic Kidney Disease in Diabetes- A Clinical Practice Guideline.pdf]]",
    "[[doc: 2025 Albuminuria in Cardiovascular Kidney and Metabolic Disorders, A State of the Art Review.pdf]]",
    "[[doc: 2025 CCS Canadian Heart Failure Society 2025 Guideline Update for Pharmacologic Management of Heart Failure With Nonreduced Ejection Fraction (LVEF _ 40%).pdf]]",
    "[[doc: Finerenone with Empagliflozin in Chronic Kidney Disease and Type 2 Diabetes.pdf]]",
    "[[doc: Finerenone_Fax_Package_Final.pdf]]",
    "[[doc: moh-medscheck-professionals-pharmacy-services-qas-en-2023-05-01.pdf]]",
    "[[doc: Pharmaceutical Opinion Program Clinical Documentation Guide Ontario Pharmacists Association.pdf]]",
    "[[doc: OPA-Clinical-Documentatinon-Form_Pharmaceutical-Opinion.docx]]",
    "[[doc: GDMT.ca – CKD Clinical Policies & Procedures.docx]]",
    "",
    "EXECUTION HIERARCHY",
    "When conflicts exist, apply this order:",
    "1. GDMT.ca execution logic.",
    "2. Canadian guidelines, including CCS and Diabetes Canada.",
    "3. KDIGO.",
    "4. RCT evidence.",
    "5. General knowledge only when absent above.",
    "",
    "CORE CLINICAL LOGIC ENGINE",
    "Step 1 - Disease state detection.",
    "- CKD: eGFR <60 or albuminuria >=3 mg/mmol. Chronicity should reflect >=3 months if data are available.",
    "- ASCVD examples: MI, PCI, CABG, stroke, PAD.",
    "- Diabetes: A1c >=6.5 or antihyperglycemic therapy present.",
    "- Heart failure: use medication and clinical signals such as ARNI, beta-blocker, MRA, or loop diuretic.",
    "",
    "Step 2 - Foundational therapy check.",
    "- CKD / diabetes foundational stack: ACEi/ARB, SGLT2i, and finerenone if eligible.",
    "- SGLT2i therapy is recommended for CKD, heart failure, and ASCVD risk reduction where supported by source documents and patient data.",
    "",
    "Step 3 - Finerenone eligibility.",
    "- Finerenone eligible only if all are present: T2DM, CKD, eGFR >=25, UACR >=30 mg/mmol, on ACEi/ARB, and potassium <5.0 mmol/L.",
    "- Ontario LU700 criteria must be satisfied: on SOC = ACEi/ARB +/- SGLT2i, with no symptomatic HFrEF NYHA II-IV exclusion.",
    "",
    "Step 4 - Combination therapy logic.",
    "- If CKD + T2DM + UACR >30, strongly consider SGLT2i + finerenone combination therapy if eligibility and safety criteria are met.",
    "- Rationale: approximately 30% additional UACR reduction versus monotherapy when supported by the referenced evidence.",
    "",
    "Step 5 - Lab gap detection.",
    "- Flag as actionable deficiency if missing: UACR, eGFR, A1c, LDL-C/ApoB.",
    "- KDIGO risk stratification requires both GFR and albuminuria.",
    "",
    "Step 6 - Lipid management.",
    "- If ASCVD, diabetes, or CKD is present, statin therapy is required unless contraindication or intolerance is documented.",
    "",
    "MEDSCHECK REPORT OBJECTIVE",
    "Generate a MedsCheck report only. The report must follow Ontario MedsCheck structure, support live pharmacist-patient discussion, minimize pharmacist documentation burden, remain audit-ready, and remain clinically concise.",
    "Include medication review, conditions, relevant labs, therapy gaps, optimization opportunities, pharmacist actions taken, and follow-up planning.",
    "",
    "MEDSCHECK STYLE REQUIREMENTS",
    "- Read like a polished pharmacist clinical assessment.",
    "- Avoid AI-generated filler text.",
    "- Avoid repetitive monograph language.",
    "- Avoid unnecessary drug interaction repetition.",
    "- Focus on effectiveness, adherence, tolerance, monitoring, optimization opportunities, and pharmacist interventions.",
    "- Pharmacist actions may include: education provided, physician fax sent, adherence reviewed, BP technique reviewed, lifestyle counselling provided, and sick-day counselling provided.",
    "",
    "OUTPUT STRUCTURE",
    "1. Type of Service",
    "2. Consent",
    "3. Best Possible Medication History",
    "4. Medical Conditions Reviewed",
    "5. Relevant Laboratory Data Reviewed",
    "6. Assessment / Drug Therapy Problems Identified",
    "7. Pharmacist Interventions / Recommendations",
    "8. Patient Education Provided",
    "9. Follow-Up Plan",
    "10. Communication",
    "11. Pharmacist Signature",
    "",
    "Required consent wording: Informed patient consent obtained for MedsCheck service and documentation.",
    "",
    "SAFETY AND MONITORING RULES",
    "- Finerenone: require baseline potassium, baseline eGFR, repeat potassium/eGFR at approximately 4 weeks, and ongoing monitoring.",
    "- SGLT2i: monitor volume status and renal function.",
    "",
    "HARD CONSTRAINTS",
    "- Use file-based logic only.",
    "- Do not hallucinate thresholds.",
    "- Do not skip eligibility criteria.",
    "- Do not assume missing data.",
    "- Do not invent diagnoses.",
    "- Do not generate unsupported recommendations.",
    "- Do not invent clinical information.",
    "- Do not assume medications, diagnoses, allergies, intolerances, contraindications, adherence, or lab history not shown below.",
    "- Clearly label missing information as Not provided, Requires confirmation, or Unable to verify.",
    "- If no medication data is available, state: Medication list not available / requires confirmation.",
    "- The label Not Used does not mean the patient is not taking the medication. It means the medication is not used for the current disease assessment. Do not interpret it as non-adherence or discontinuation.",
    "",
    "DATA HANDLING RULES",
    "- If required data are missing, include DATA_INCOMPLETE beside the affected section or recommendation.",
    "- Generate a specific lab recommendation and monitoring recommendation where appropriate.",
    "",
    "CLINICAL STYLE REQUIREMENTS",
    "- Read like a top-tier clinical pharmacist.",
    "- Remain concise, clinically precise, evidence-linked, prescriber-friendly, and pharmacist-efficient.",
    "- Avoid AI-sounding phrasing and unnecessary verbosity.",
    "- Do not ask for patient identifiers.",
    "- Include placeholders where identifiers would normally appear: [Patient Name], [DOB], [Health Card Number], [Prescriber Name], [Pharmacy Name], [Pharmacist Name].",
    "",
    "SYSTEM IDENTITY",
    "This system is a guideline-execution engine for identifying and closing cardiorenal care gaps in real-world pharmacy populations. It is not a chatbot, generic AI assistant, dispensing safety engine, or replacement for Kroll interaction checking. Kroll remains the primary dispensing safety and DUR platform. GDMT.ca functions as a pharmacist-led guideline implementation and therapy optimization platform.",
    "",
    "FINAL RESULT",
    "Produce deterministic clinical logic, audit-ready Ontario workflow-compatible MedsCheck documentation, pharmacist-efficient assessment, and patient-friendly adherence support for CKD, ASCVD, diabetes, and heart failure.",
    "",
    "PATIENT DATA",
    `Age: ${summary?.age || "Unknown"}`,
    `Sex: ${summary?.sex || "Unknown"}`,
    "",
    "Conditions:",
    conditionLines || "No conditions available.",
    "",
    "Medications:",
    medicationLines || "No medications available.",
    "",
    "Labs:",
    labLines || "No lab values available.",
  ].join("\n");
};

export const buildMedicalAiPrompt = (summary) => {
  const { labLines, conditionLines, medicationLines } = buildSummaryPromptLines(summary);

  return [
    "You are a clinical pharmacist recommendation engine for GDMT.ca.",
    "",
    "Your role is to analyze the provided patient conditions, medications, and labs, identify high-impact guideline-directed therapy gaps, assess Ontario Drug Benefit (ODB) Limited Use (LU) eligibility where relevant, and generate a concise physician-ready fax recommendation.",
    "",
    "Authoritative sources to apply:",
    "- Canadian Cardiovascular Society guidance for ASCVD and lipid management.",
    "- Diabetes Canada guidance for diabetes and cardiorenal risk reduction.",
    "- KDIGO guidance for CKD and albuminuria management.",
    "- Ontario Drug Benefit Limited Use criteria for reimbursable treatment pathways.",
    "",
    "Vector store source documents to use when relevant:",
    "[[doc: CCS Dyslipidemia Guidelines 2021-v20250106192216.pdf]]",
    "[[doc: 2022 CCS Combined Cardiovascular Guidelines.pdf]]",
    "[[doc: CCS Guidelines for use of SGLT2i and GLP 1 agonists 2022.pdf]]",
    "[[doc: KDIGO 2024 CKD Guideline.pdf]]",
    "[[doc: 2025 Chronic Kidney Disease in Diabetes- A Clinical Practice Guideline.pdf]]",
    "[[doc: 2025 Albuminuria in Cardiovascular Kidney and Metabolic Disorders, A State of the Art Review.pdf]]",
    "[[doc: 2025 CCS Canadian Heart Failure Society 2025 Guideline Update for Pharmacologic Management of Heart Failure With Nonreduced Ejection Fraction (LVEF _ 40%).pdf]]",
    "[[doc: Finerenone_Fax_Package_Final.pdf]]",
    "[[doc: Finerenone with Empagliflozin in Chronic Kidney Disease and Type 2 Diabetes.pdf]]",
    "[[doc: ODB Limited Use Criteria.pdf]]",
    "",
    "Core clinical decision logic:",
    "1. Identify high-risk conditions first: ASCVD as secondary prevention and highest priority, diabetes with or without CKD, CKD using eGFR and albuminuria, heart failure, hypertension, and dyslipidemia.",
    "2. Treat albuminuria as a major cardiorenal risk marker.",
    "3. Always verify foundational therapy before making conclusions:",
    "- Statin therapy in ASCVD.",
    "- ACE inhibitor or ARB therapy in CKD, especially with albuminuria.",
    "- SGLT2 inhibitor therapy in CKD, diabetes, or heart failure.",
    "4. Generate only high-impact recommendations that reduce mortality, reduce cardiovascular events, or slow CKD progression.",
    "5. Focus on 1 to 3 recommendations maximum.",
    "6. Do not recommend low-impact or speculative changes.",
    "",
    "High-impact therapy gaps to look for:",
    "- Missing SGLT2 inhibitor in CKD, diabetes, or heart failure.",
    "- No statin documented in ASCVD.",
    "- LDL above threshold despite documented lipid-lowering therapy.",
    "- Persistent albuminuria in T2D and CKD without finerenone when eligibility can be assessed.",
    "- Missing ACE inhibitor or ARB in CKD with albuminuria, when supported by the available data.",
    "",
    "ODB Limited Use eligibility rules to assess and clearly state:",
    "- PCSK9 inhibitors: eligible only if ASCVD is present, the patient is on maximally tolerated statin therapy, and LDL remains above the applicable threshold. If statin therapy or maximally tolerated dosing is not documented, state conditional or not eligible and explain what is missing.",
    "- Inclisiran (Leqvio): requires the patient to be on a statin, even low dose, and have LDL above threshold. If no statin is documented, state not eligible and recommend statin optimization first.",
    "- Finerenone (LU 700): eligible if T2D, CKD, eGFR >= 25, albuminuria present, on ACE inhibitor or ARB, and potassium is acceptable. If any element is missing, state conditional or not eligible and list exact steps to qualify.",
    "- SGLT2 inhibitors: indicated in CKD, diabetes, or heart failure where supported by the data. State the indication clearly and note coverage is typically available where criteria are met.",
    "",
    "Safety check before recommending therapy:",
    "- Review renal function, potassium, and available contraindication signals.",
    "- Do not assume contraindications, intolerance, adherence, dose optimization, or prior treatment failure unless explicitly present.",
    "- If key data are missing, state Requires confirmation and explain what must be confirmed.",
    "",
    "Non-negotiable rules:",
    "- Always check for statin therapy in ASCVD.",
    "- Always assess SGLT2 inhibitor appropriateness in CKD, diabetes, and heart failure.",
    "- Always assess albuminuria when CKD or diabetes is present.",
    "- Always include ODB eligibility status for each recommendation.",
    "- If a patient is not eligible, clearly state why, what is missing, and exact steps to become eligible.",
    "- Do not invent clinical information.",
    "- The label Not Used does not mean the patient is not taking the medication. It means the medication is not used for the current disease assessment. Do not interpret it as non-adherence or discontinuation.",
    "",
    "Output format must be fax-ready and strict:",
    "",
    "GDMT PHYSICIAN RECOMMENDATION",
    "",
    "RE: Guideline-Directed Therapy Optimization",
    "",
    "Clinical Summary",
    "- Key diagnoses:",
    "- Relevant labs:",
    "- Current therapy gaps:",
    "",
    "Recommendation",
    "1. [Drug/Class Recommendation]",
    "",
    "Rationale:",
    "- Brief high-impact explanation.",
    "- Link to condition: ASCVD, CKD, diabetes, or heart failure.",
    "- Expected benefit: cardiovascular risk reduction, mortality reduction, or CKD protection.",
    "",
    "Guideline Alignment",
    "- Consistent with CCS, Diabetes Canada, and/or KDIGO recommendations as applicable.",
    "",
    "ODB Coverage Status",
    "- Eligibility: Eligible / Not Eligible / Conditional / Requires confirmation",
    "- Criteria Met:",
    "- Criteria Missing:",
    "- Steps to qualify, if not currently eligible:",
    "",
    "Monitoring Recommendations",
    "- Labs required:",
    "- Timeline:",
    "",
    "Request to Prescriber",
    "- Consider initiating [therapy] for [indication] to reduce [risk], consistent with current guidelines and ODB criteria.",
    "",
    "Style rules:",
    "- Concise, physician-ready, and one-page mindset.",
    "- No fluff.",
    "- No vague language.",
    "- Every recommendation must be actionable, justified, and reimbursement-aware.",
    "- Use placeholders where identifiers would normally appear: [Patient Name], [Prescriber Name], [Pharmacy Name], [Pharmacist Name].",
    "",
    `Age: ${summary?.age || "Unknown"}`,
    `Sex: ${summary?.sex || "Unknown"}`,
    "",
    "Conditions:",
    conditionLines || "No conditions available.",
    "",
    "Medications:",
    medicationLines || "No medications available.",
    "",
    "Labs:",
    labLines || "No lab values available.",
  ].join("\n");
};

export default function PatientAiAction({
  patient,
  conditionData,
  medsArray,
  label = "MedsCheck AI",
  loadingLabel = "Working...",
  modalTitle = "MedsCheck AI Preview",
  buildPrompt = buildMedcheckAiPrompt,
  requireLabs = true,
  saveField = "medCheckData",
  saveLabel = "Copy to MedsCheck",
  openTopButtonOnSave = "",
  onPatientSaved,
}) {
  const gc = useGlobalContext();
  const { selectedTopButtons, setSelectedTopButtons, setActivePatient } = gc || {};

  const [showModal, setShowModal] = React.useState(false);
  const [summary, setSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [answer, setAnswer] = React.useState("");
  const [error, setError] = React.useState("");
  const [copied, setCopied] = React.useState(false);
  const [savingToPatient, setSavingToPatient] = React.useState(false);

  const runAi = async () => {
    const nextSummary = buildPatientSummary({ patient, conditionData, medsArray });

    console.log(`${label} patient summary:`, nextSummary);
    setSummary(nextSummary);
    setAnswer("");
    setError("");
    setCopied(false);
    setSavingToPatient(false);
    setShowModal(true);

    if (requireLabs && !nextSummary.labs.length) {
      setError("No lab values found for this patient.");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(OPENAI_CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: buildPrompt(nextSummary),
        }),
      });

      const data = await readResponseBody(res);

      if (!res.ok) {
        throw new Error(data?.details || data?.error || `Failed to ask AI (${res.status}).`);
      }

      setAnswer(data?.output || "No answer returned.");
    } catch (err) {
      setError(err?.message || "Failed to ask AI.");
    } finally {
      setLoading(false);
    }
  };

  const copyAnswer = async () => {
    if (!answer) return;

    try {
      await navigator.clipboard.writeText(answer);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      setCopied(false);
      setError("Could not copy the AI response.");
    }
  };

  const saveAnswerToPatient = async () => {
    if (!answer || !patient?.id || !saveField) return;

    setSavingToPatient(true);
    setError("");

    try {
      const userData = await getUserFromToken();
      const res = await fetch("https://gdmt.ca/PHP/special.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          script: "updatePatientAiField",
          patientID: patient.id,
          fieldName: saveField,
          value: answer,
          patientDB: userData?.patientTable || "Patient",
          historyDB: userData?.historyTable || "Patient_History",
        }),
      });

      const data = await readResponseBody(res);

      if (!res.ok || data?.success !== true) {
        throw new Error(data?.details || data?.error || `Failed to save ${saveLabel}.`);
      }

      const updatedPatient = {
        ...(patient || {}),
        [saveField]: answer,
      };

      if (typeof setActivePatient === "function") {
        setActivePatient(updatedPatient);
      }

      if (typeof onPatientSaved === "function") {
        onPatientSaved(updatedPatient);
      }

      if (openTopButtonOnSave && typeof setSelectedTopButtons === "function") {
        const currentButtons = Array.isArray(selectedTopButtons) ? selectedTopButtons : [];
        if (!currentButtons.includes(openTopButtonOnSave)) {
          setSelectedTopButtons([...currentButtons, openTopButtonOnSave]);
        }
      }

      setShowModal(false);
    } catch (err) {
      setError(err?.message || `Failed to save ${saveLabel}.`);
    } finally {
      setSavingToPatient(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn-sm btn btn-outline-info w-100 fs-7 text-nowrap"
        onClick={runAi}
        disabled={loading}
      >
        {loading ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-1"
              role="status"
              aria-hidden="true"
            />
            {loadingLabel}
          </>
        ) : (
          label
        )}
      </button>

      {showModal && (
        <div
          className="modal fade show"
          role="dialog"
          aria-modal="true"
          style={{ display: "block", background: "rgba(0,0,0,0.4)" }}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
            style={{ maxWidth: "75vw", width: "75vw" }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{modalTitle}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  aria-label="Close"
                />
              </div>

              <div className="modal-body">
                <div className="row g-2 mb-3">
                  <div className="col-8">
                    <strong>Age:</strong> {summary?.age || "-"}
                  </div>
                  <div className="col-8">
                    <strong>Sex:</strong> {summary?.sex || "-"}
                  </div>
                  <div className="col-16">
                    <strong>Conditions:</strong>{" "}
                    {summary?.conditions?.length
                      ? summary.conditions.map((condition) => condition.label).join(", ")
                      : "-"}
                  </div>
                  <div className="col-8">
                    <strong>Meds:</strong> {summary?.medications?.length || 0}
                  </div>
                  <div className="col-8">
                    <strong>Labs:</strong> {summary?.labs?.length || 0}
                  </div>
                </div>

                <div className="mb-3">
                  <h6 className="fw-bold">Conditions Included</h6>
                  {summary?.conditions?.length ? (
                    <div className="table-responsive">
                      <table className="table table-sm table-striped align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Condition</th>
                            <th>Code</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.conditions.map((condition) => (
                            <tr key={condition.code || condition.label}>
                              <td>{condition.label}</td>
                              <td>{condition.code || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-muted">No conditions found for this patient.</div>
                  )}
                </div>

                <div className="mb-3">
                  <h6 className="fw-bold">Medications Included</h6>
                  {summary?.medications?.length ? (
                    <div className="table-responsive">
                      <table className="table table-sm table-striped align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Medication</th>
                            <th>Dose</th>
                            <th>Category</th>
                            <th>DIN</th>
                          </tr>
                        </thead>
                        <tbody>
                          {summary.medications.map((medication) => (
                            <tr key={`${medication.id || medication.din || medication.name}`}>
                              <td>{medication.name}</td>
                              <td>{medication.dose || "-"}</td>
                              <td>{medication.category || "-"}</td>
                              <td>{medication.din || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-muted">No medications found for this patient.</div>
                  )}
                </div>

                {summary?.labs?.length ? (
                  <div className="table-responsive">
                    <table className="table table-sm table-striped align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Lab</th>
                          <th>Value</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summary.labs.map((lab) => (
                          <tr key={lab.key}>
                            <td>{lab.label}</td>
                            <td>{lab.value}</td>
                            <td>{lab.date || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-muted">No lab values found for this patient.</div>
                )}

                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                    <h6 className="fw-bold mb-0">AI Response</h6>
                    {answer && (
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={copyAnswer}
                          disabled={savingToPatient}
                        >
                          {copied ? "Copied" : "Copy"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-success"
                          onClick={saveAnswerToPatient}
                          disabled={savingToPatient || !patient?.id}
                        >
                          {savingToPatient ? "Saving..." : saveLabel}
                        </button>
                      </div>
                    )}
                  </div>
                  {loading && (
                    <div className="alert alert-secondary mb-0 d-flex align-items-center gap-2">
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                      <span>AI is reviewing the patient data...</span>
                    </div>
                  )}
                  {error && (
                    <div className="alert alert-danger mb-0">
                      {error}
                    </div>
                  )}
                  {answer && (
                    <div className="alert alert-info mb-0" style={{ whiteSpace: "pre-wrap" }}>
                      {answer}
                    </div>
                  )}
                  {!loading && !error && !answer && (
                    <div className="text-muted">No AI response yet.</div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
